import { useEffect, useRef, useState } from 'react';
import {
  API_PRESETS,
  RedesignError,
  inspectWorkflow,
  loadCredential,
  parseWorkflow,
  saveCredential,
  suggestBindings,
  type RedesignProgress,
} from '@design-ai/redesign';
import type { Engine } from '../engine.js';
import {
  applyPreset,
  buildTarget,
  loadConfig,
  saveConfig,
  type RedesignConfig,
  type TargetKind,
} from '../redesign-config.js';
import { ProgressBar, Section, Select, Slider, Toggle } from './controls.js';

export interface RedesignPanelProps {
  engine: Engine;
  ready: boolean;
  /** Called after a result is adopted as the new document. */
  onAdopted: () => void;
}

interface ResultImage {
  blob: Blob;
  url: string;
}

/**
 * The handoff to the redesign backend.
 *
 * This is the point where images leave the machine, so the panel is explicit
 * about where they are going and shows the endpoint next to the run button.
 */
export function RedesignPanel({ engine, ready, onAdopted }: RedesignPanelProps) {
  const [config, setConfig] = useState<RedesignConfig>(() => loadConfig());
  const [apiKey, setApiKey] = useState(() => loadCredential('comfyui'));
  const [progress, setProgress] = useState<RedesignProgress | null>(null);
  const [error, setError] = useState<{ message: string; hint?: string } | null>(null);
  const [results, setResults] = useState<ResultImage[]>([]);
  const abortRef = useRef<AbortController | null>(null);

  const credentialId = config.targetKind === 'comfy' ? 'comfyui' : 'http-api';

  useEffect(() => {
    saveConfig(config);
  }, [config]);

  useEffect(() => {
    setApiKey(loadCredential(credentialId));
  }, [credentialId]);

  // Object URLs for the previews have to be revoked or they leak per run.
  useEffect(() => () => results.forEach((result) => URL.revokeObjectURL(result.url)), [results]);

  const update = (patch: Partial<RedesignConfig>): void => setConfig((prev) => ({ ...prev, ...patch }));

  const run = async (): Promise<void> => {
    setError(null);
    results.forEach((result) => URL.revokeObjectURL(result.url));
    setResults([]);

    let target;
    try {
      target = buildTarget(config);
    } catch (cause) {
      setError({ message: cause instanceof Error ? cause.message : String(cause) });
      return;
    }

    const controller = new AbortController();
    abortRef.current = controller;
    setProgress({ stage: 'uploading', fraction: null });

    try {
      const assets = await engine.workbench.exportForRedesign({
        trim: config.trim,
        ...(config.maxEdge > 0 ? { maxEdge: config.maxEdge } : {}),
      });

      const result = await target.run(
        {
          image: assets.image,
          mask: assets.mask,
          prompt: config.prompt,
          negativePrompt: config.negativePrompt,
          width: assets.width,
          height: assets.height,
        },
        { onProgress: setProgress, signal: controller.signal },
      );

      setResults(result.images.map((blob) => ({ blob, url: URL.createObjectURL(blob) })));
    } catch (cause) {
      if (cause instanceof DOMException && cause.name === 'AbortError') return;
      setError(
        cause instanceof RedesignError
          ? { message: cause.message, ...(cause.hint ? { hint: cause.hint } : {}) }
          : { message: cause instanceof Error ? cause.message : String(cause) },
      );
    } finally {
      abortRef.current = null;
      setProgress(null);
    }
  };

  /** Loads a result back in as the new document, so it can be re-cut or upscaled. */
  const adopt = async (result: ResultImage): Promise<void> => {
    const bitmap = await createImageBitmap(result.blob);
    engine.workbench.loadSource(bitmap, bitmap.width, bitmap.height);
    bitmap.close();
    onAdopted();
  };

  let endpointLabel = '';
  try {
    endpointLabel = buildTarget(config).endpoint;
  } catch {
    endpointLabel = 'not configured';
  }

  return (
    <Section title="Redesign" subtitle="Sends the cut-out to a backend. This leaves your machine.">
      <Select<TargetKind>
        label="Backend"
        value={config.targetKind}
        options={[
          { value: 'comfy', label: 'ComfyUI workflow' },
          { value: 'http', label: 'Image API' },
        ]}
        onChange={(targetKind) => update({ targetKind })}
      />

      <label className="control">
        <span className="control__label">Prompt</span>
        <textarea
          className="textarea"
          rows={3}
          value={config.prompt}
          onChange={(event) => update({ prompt: event.target.value })}
          placeholder="How the design should be reworked"
        />
      </label>

      <label className="control">
        <span className="control__label">Negative prompt</span>
        <textarea
          className="textarea"
          rows={2}
          value={config.negativePrompt}
          onChange={(event) => update({ negativePrompt: event.target.value })}
        />
      </label>

      <label className="control">
        <span className="control__label">
          API key
          <em>{apiKey ? 'stored' : 'none'}</em>
        </span>
        <input
          type="password"
          className="input"
          value={apiKey}
          onChange={(event) => {
            setApiKey(event.target.value);
            saveCredential(credentialId, event.target.value);
          }}
          placeholder={config.targetKind === 'comfy' ? 'blank for self-hosted ComfyUI' : 'required'}
        />
        <small className="control__hint">
          Kept in localStorage on this origin, because the browser calls the backend directly. Use a
          key scoped to this tool.
        </small>
      </label>

      {config.targetKind === 'comfy' ? (
        <ComfySettingsEditor config={config} update={update} />
      ) : (
        <HttpSettingsEditor config={config} update={update} />
      )}

      <details className="details">
        <summary>What gets sent</summary>
        <div className="details__body">
          <Toggle
            label="Trim transparent margin"
            checked={config.trim}
            onChange={(trim) => update({ trim })}
          />
          <Slider
            label="Max edge"
            value={config.maxEdge}
            min={0}
            max={4096}
            step={256}
            onChange={(maxEdge) => update({ maxEdge })}
            format={(value) => (value === 0 ? 'full size' : `${value}px`)}
            hint="A generative backend resamples anyway, so this is mostly transfer size."
          />
          <p className="control__hint">
            The cut-out goes as an RGBA PNG and the matte as a separate greyscale PNG, which is what
            both ComfyUI and inpainting endpoints expect.
          </p>
        </div>
      </details>

      <p className="control__hint">Sending to: {endpointLabel}</p>

      {progress ? (
        <>
          <ProgressBar
            value={progress.fraction}
            label={`${progress.stage}${progress.message ? `: ${progress.message}` : ''}`}
          />
          <button type="button" className="button" onClick={() => abortRef.current?.abort()}>
            Cancel
          </button>
        </>
      ) : (
        <button type="button" className="button button--primary" disabled={!ready} onClick={() => void run()}>
          Redesign
        </button>
      )}

      {error ? (
        <div className="error">
          <p className="error">{error.message}</p>
          {error.hint ? <p className="control__hint">{error.hint}</p> : null}
        </div>
      ) : null}

      {results.length > 0 ? (
        <div className="results">
          {results.map((result, index) => (
            <figure key={result.url} className="results__item">
              <img src={result.url} alt={`Result ${index + 1}`} />
              <figcaption>
                <button type="button" className="button" onClick={() => void adopt(result)}>
                  Use as source
                </button>
                <a className="button" href={result.url} download={`redesign-${index + 1}.png`}>
                  Download
                </a>
              </figcaption>
            </figure>
          ))}
        </div>
      ) : null}
    </Section>
  );
}

function ComfySettingsEditor({
  config,
  update,
}: {
  config: RedesignConfig;
  update: (patch: Partial<RedesignConfig>) => void;
}) {
  const { comfy } = config;
  const [workflowError, setWorkflowError] = useState<string | null>(null);

  /** Re-derives bindings whenever a new workflow is pasted. */
  const setWorkflow = (workflowText: string): void => {
    if (!workflowText.trim()) {
      setWorkflowError(null);
      update({ comfy: { ...comfy, workflowText, bindings: {} } });
      return;
    }
    try {
      const workflow = parseWorkflow(workflowText);
      setWorkflowError(null);
      update({ comfy: { ...comfy, workflowText, bindings: suggestBindings(workflow) } });
    } catch (cause) {
      setWorkflowError(cause instanceof Error ? cause.message : String(cause));
      update({ comfy: { ...comfy, workflowText } });
    }
  };

  let summary: string | null = null;
  if (comfy.workflowText.trim() && !workflowError) {
    try {
      const found = inspectWorkflow(parseWorkflow(comfy.workflowText));
      summary = `${found.nodes.length} nodes, image node ${comfy.bindings.imageNode ?? '?'}, prompt node ${comfy.bindings.promptNode ?? '?'}`;
    } catch {
      summary = null;
    }
  }

  return (
    <>
      <label className="control">
        <span className="control__label">ComfyUI URL</span>
        <input
          type="text"
          className="input"
          value={comfy.baseUrl}
          onChange={(event) => update({ comfy: { ...comfy, baseUrl: event.target.value } })}
          placeholder="http://127.0.0.1:8188"
        />
        <small className="control__hint">
          Self-hosted ComfyUI must be started with --enable-cors-header, since the page calls it
          directly.
        </small>
      </label>

      <label className="control">
        <span className="control__label">
          Workflow (API format)
          {summary ? <em>{summary}</em> : null}
        </span>
        <textarea
          className="textarea textarea--code"
          rows={5}
          value={comfy.workflowText}
          onChange={(event) => setWorkflow(event.target.value)}
          placeholder='Paste the JSON from ComfyUI "Save (API format)"'
        />
        <small className="control__hint">
          Bindings are derived from the graph: the LoadImage node takes the cut-out, and the prompt
          nodes are found by following the sampler&apos;s positive/negative links.
        </small>
      </label>
      {workflowError ? <p className="error">{workflowError}</p> : null}

      <details className="details">
        <summary>Bindings</summary>
        <div className="details__body">
          {(
            [
              ['imageNode', 'Image node'],
              ['promptNode', 'Prompt node'],
              ['negativeNode', 'Negative node'],
              ['maskNode', 'Mask node'],
              ['seedNode', 'Seed node'],
            ] as const
          ).map(([key, label]) => (
            <label className="control" key={key}>
              <span className="control__label">{label}</span>
              <input
                type="text"
                className="input"
                value={comfy.bindings[key] ?? ''}
                onChange={(event) =>
                  update({
                    comfy: {
                      ...comfy,
                      bindings: { ...comfy.bindings, [key]: event.target.value || undefined },
                    },
                  })
                }
                placeholder="node id"
              />
            </label>
          ))}
        </div>
      </details>
    </>
  );
}

function HttpSettingsEditor({
  config,
  update,
}: {
  config: RedesignConfig;
  update: (patch: Partial<RedesignConfig>) => void;
}) {
  const { http } = config;

  return (
    <>
      <Select
        label="Shape"
        value={http.presetId}
        options={Object.entries(API_PRESETS).map(([id, preset]) => ({
          value: id,
          label: preset.label ?? id,
        }))}
        onChange={(presetId) => update({ http: applyPreset(http, presetId) })}
        hint="A starting point for the request shape - check the field names against your provider's docs."
      />

      <label className="control">
        <span className="control__label">Endpoint</span>
        <input
          type="text"
          className="input"
          value={http.endpoint}
          onChange={(event) => update({ http: { ...http, endpoint: event.target.value } })}
          placeholder="https://api.example.com/v1/images/edits"
        />
      </label>

      <details className="details">
        <summary>Request fields</summary>
        <div className="details__body">
          {(
            [
              ['image', 'Image field'],
              ['mask', 'Mask field'],
              ['prompt', 'Prompt field'],
              ['negativePrompt', 'Negative field'],
              ['seed', 'Seed field'],
            ] as const
          ).map(([key, label]) => (
            <label className="control" key={key}>
              <span className="control__label">{label}</span>
              <input
                type="text"
                className="input"
                value={http.fields[key] ?? ''}
                onChange={(event) =>
                  update({
                    http: {
                      ...http,
                      fields: { ...http.fields, [key]: event.target.value || undefined },
                    },
                  })
                }
                placeholder="omit to leave out"
              />
            </label>
          ))}

          <label className="control">
            <span className="control__label">Extra body fields (JSON)</span>
            <textarea
              className="textarea textarea--code"
              rows={3}
              value={http.extraText}
              onChange={(event) => update({ http: { ...http, extraText: event.target.value } })}
            />
          </label>

          <label className="control">
            <span className="control__label">Response path</span>
            <input
              type="text"
              className="input"
              value={http.responsePath}
              onChange={(event) => update({ http: { ...http, responsePath: event.target.value } })}
              placeholder="data[].b64_json"
            />
            <small className="control__hint">
              Leave blank when the endpoint returns image bytes. `[]` maps over an array.
            </small>
          </label>
        </div>
      </details>
    </>
  );
}
